import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import { Bold, Italic, Underline as UnderlineIcon, Link2, List, ListOrdered, Heading2, Heading3, Undo2, Redo2, Quote } from "lucide-react";

const VARIABLES = [
    { key: "first_name", label: "Nome" },
    { key: "last_name", label: "Cognome" },
    { key: "studio", label: "Studio" },
    { key: "city", label: "Città" },
];

export function TipTapEditor({ value, onChange, placeholder = "Scrivi la tua newsletter..." }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-primary underline underline-offset-2" } }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
        editorProps: {
            attributes: {
                class: "prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[220px] px-4 py-3 text-[14px] leading-[1.7]",
            },
        },
    });

    useEffect(() => {
        if (editor && value !== editor.getHTML() && value !== undefined) {
            editor.commands.setContent(value || "", false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    if (!editor) return null;

    const btn = "h-7 w-7 rounded-md hover:bg-surface2 flex items-center justify-center text-textmuted hover:text-textmain transition-colors";
    const btnActive = "h-7 w-7 rounded-md bg-primary/[0.08] text-primary flex items-center justify-center";
    const cls = (active) => (active ? btnActive : btn);

    const setLink = () => {
        const url = window.prompt("URL del link", editor.getAttributes("link").href || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    const insertVar = (k) => {
        editor.chain().focus().insertContent(`{{${k}}}`).run();
    };

    return (
        <div className="rounded-xl border border-border/[var(--border-opacity)] bg-surface overflow-hidden">
            <div className="flex items-center gap-0.5 border-b border-border/[var(--border-opacity)] px-2 py-1.5 flex-wrap">
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={cls(editor.isActive("bold"))} title="Grassetto"><Bold size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={cls(editor.isActive("italic"))} title="Corsivo"><Italic size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={cls(editor.isActive("underline"))} title="Sottolineato"><UnderlineIcon size={13} strokeWidth={1.5} /></button>
                <div className="w-px h-4 mx-1 bg-border/[var(--border-opacity)]" />
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={cls(editor.isActive("heading", { level: 2 }))} title="Titolo"><Heading2 size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={cls(editor.isActive("heading", { level: 3 }))} title="Sottotitolo"><Heading3 size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={cls(editor.isActive("bulletList"))} title="Elenco puntato"><List size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={cls(editor.isActive("orderedList"))} title="Elenco numerato"><ListOrdered size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={cls(editor.isActive("blockquote"))} title="Citazione"><Quote size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={setLink} className={cls(editor.isActive("link"))} title="Link"><Link2 size={13} strokeWidth={1.5} /></button>
                <div className="w-px h-4 mx-1 bg-border/[var(--border-opacity)]" />
                <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btn} title="Annulla"><Undo2 size={13} strokeWidth={1.5} /></button>
                <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btn} title="Ripristina"><Redo2 size={13} strokeWidth={1.5} /></button>
                <div className="w-px h-4 mx-1 bg-border/[var(--border-opacity)]" />
                {VARIABLES.map((v) => (
                    <button
                        key={v.key}
                        type="button"
                        onClick={() => insertVar(v.key)}
                        className="text-[10.5px] px-2 py-0.5 rounded-md bg-primary/[0.06] text-primary hover:bg-primary/[0.12] transition-colors pv-mono"
                        title={`Inserisci variabile ${v.label}`}
                    >{`{{${v.key}}}`}</button>
                ))}
            </div>
            <EditorContent editor={editor} />
        </div>
    );
}
